import classnames from 'classnames'
import type {Ref} from 'react'
import * as React from 'react'
import {Suspense} from 'react'

import RunningBuildUpdater from '../../../../containers/RunningBuildUpdater'
import {BuildLineLayoutContext} from '../../../../contexts/buildLineLayout'
import {useOneLine} from '../../../../contexts/oneLine'
import {ApprovalStatuses} from '../../../../services/rest'
import BuildChangesDropdown from '../../../packages/Changes/BuildChangesDropdown/BuildChangesDropdown'
import AgentLink from '../../AgentLink/BuildAgentLink.container'
import BuildBranch from '../../BranchLabel/BuildBranchLabel.container'
import OverviewBuildActions from '../../BuildActionsDropdown/BuildActionsDropdown'
import ApprovalInfo from '../../BuildApproval/BuildApproval.container'
import {BuildApprovalViewMode} from '../../BuildApproval/BuildApproval.types'
import BuildArtifacts from '../../BuildArtifacts/BuildArtifacts.container'
import BuildCommentByIcon from '../../BuildCommentByIcon/BuildCommentByIcon.container'
import BuildDuration from '../../BuildDuration/BuildDuration.container'
import BuildDurationEstimate from '../../BuildDurationEstimate/BuildDurationEstimate'
import BuildNumberContainer from '../../BuildNumber/BuildNumber.container'
import BuildPath from '../../BuildPath/BuildPath.container'
import BuildStatus from '../../BuildStatus/BuildStatus.container'
import BuildStatusChangeComment from '../../BuildStatusChangeComment/BuildStatusChangeComment'
import BuildTriggeredBy from '../../BuildTriggeredBy/BuildTriggeredBy.container'
import CompatibleAgents from '../../CompatibleAgents/CompatibleAgents.container'
import StopBuild from '../../StopBuild/StopBuild.container'
import {useVisibility} from '../../Visible/Visible.hooks'

import type {Props} from './Build.types'
import BuildCheckbox from './BuildCheckbox/BuildCheckbox'
import BuildDependants from './BuildDependants/BuildDependants.container'
import BuildLayout from './BuildLayout/BuildLayout'
import BuildTagDropdown from './BuildTagDropdown/BuildTagDropdown'
import DownloadedDeliveredArtifacts from './DownloadedDeliveredArtifacts/DownloadedDeliveredArtifacts'
import PinBuild from './PinBuild/PinBuild.container'

import styles from './Build.css'

function Build(
  {
    className,
    alwaysShowLineBorders = false,
    buildId,
    buildTypeId,
    hasBranch,
    hideBorders,
    state,
    composite,
    tagsNumber,
    wrapped,
    pathFilter,
    inHierarchy,
    approvalStatus,
    visible: outerIsVisible,
  }: Props,
  outerVisibilityRef: Ref<HTMLDivElement>,
) {
  const oneLine = useOneLine()
  const {
    showAgent,
    showExactDuration,
    showTags,
    showBranch,
    showPath,
    showDuration,
    artifactDependenciesMode,
  } = React.useContext(BuildLineLayoutContext)
  const inQueue = state === 'queued'
  const classes = classnames(styles.build, className, {
    [styles.withoutPath]: !showPath,
    [styles.wrapped]: wrapped,
    [styles.queued]: inQueue,
    [styles.inHierarchy]: inHierarchy,
  })
  const renderPath = showPath === true && buildTypeId != null
  const renderStatusAfterBranch = !renderPath && inQueue && !oneLine
  const innerVisibilityRef = React.useRef(null)
  const innerIsVisible = useVisibility(
    innerVisibilityRef,
    undefined,
    false,
    outerVisibilityRef != null,
  )

  const durationContent = React.useMemo(() => {
    if (
      inQueue &&
      approvalStatus &&
      [ApprovalStatuses.WAITING_FOR_APPROVAL, ApprovalStatuses.TIMED_OUT].includes(approvalStatus)
    ) {
      return <ApprovalInfo buildId={buildId} view={BuildApprovalViewMode.POPUP} />
    }

    return inQueue ? (
      <BuildDurationEstimate />
    ) : (
      <BuildDuration className={styles.duration} buildId={buildId} exact={showExactDuration} />
    )
  }, [approvalStatus, buildId, inQueue, showExactDuration])
  return oneLine == null ? null : (
    <>
      <BuildCheckbox wrapped={wrapped} />
      {state === 'running' && (outerVisibilityRef != null ? outerIsVisible : innerIsVisible) && (
        <RunningBuildUpdater buildId={buildId} />
      )}
      <BuildLayout
        className={classes}
        hideBorders={hideBorders}
        alwaysShowLineBorders={alwaysShowLineBorders}
        inQueue={inQueue}
      >
        <div className={styles.notCommentWrapper}>
          <div ref={outerVisibilityRef || innerVisibilityRef} className={styles.number}>
            <BuildNumberContainer buildId={buildId} withWarnings />
          </div>
          {(showBranch === true || showPath || renderStatusAfterBranch) && (
            <div
              className={classnames(styles.branchPath, {
                [styles.noBranch]: !hasBranch && !renderStatusAfterBranch,
              })}
            >
              {showBranch === true && <BuildBranch className={styles.branch} buildId={buildId} />}
              {renderPath && (
                <BuildPath
                  buildId={buildId}
                  leafBuildTypeId={buildTypeId}
                  className={styles.path}
                  withCollapsing
                  projectOrBuildTypeNode={pathFilter}
                />
              )}
              {renderStatusAfterBranch && (
                <BuildStatus buildTypeId={buildTypeId} buildId={buildId} state={state} />
              )}
            </div>
          )}
          {!renderStatusAfterBranch && (
            <div className={styles.status}>
              <BuildStatus buildTypeId={buildTypeId} buildId={buildId} state={state} />
            </div>
          )}
          {artifactDependenciesMode ? (
            <div className={styles.downloadedDeliveredArtifacts}>
              <Suspense>
                <DownloadedDeliveredArtifacts buildId={buildId} />
              </Suspense>
            </div>
          ) : (
            <>
              <div className={styles.changes}>
                <BuildChangesDropdown buildId={buildId} />
              </div>
              {showAgent === true && composite !== true && (
                <div className={styles.agent}>
                  {inQueue ? (
                    <CompatibleAgents buildId={buildId} />
                  ) : (
                    <AgentLink buildId={buildId} />
                  )}
                </div>
              )}
              <div className={styles.durationIcons}>
                {showDuration === true && (
                  <div
                    className={classnames(styles.duration, {
                      [styles.exactDuration]: showExactDuration,
                    })}
                  >
                    {durationContent}
                    {state !== 'finished' && !inQueue && (
                      <StopBuild className={styles.stop} buildId={buildId} withAgent />
                    )}
                  </div>
                )}

                {showTags === true && tagsNumber > 0 && (
                  <div className={styles.tags}>
                    <BuildTagDropdown
                      className={styles.tag}
                      buildId={buildId}
                      tagsNumber={tagsNumber}
                    />
                  </div>
                )}
                <div className={styles.icons}>
                  {!inQueue && <PinBuild buildId={buildId} />}
                  <BuildDependants buildId={buildId} />
                  {inQueue && <StopBuild buildId={buildId} withAgent />}
                  {!inQueue && <BuildArtifacts buildId={buildId} />}
                  <OverviewBuildActions key={String(state === 'running')} fromBuildList compact />
                </div>
              </div>
            </>
          )}
        </div>
        <div
          className={classnames(styles.description, {
            [styles.compactDescription]: renderStatusAfterBranch,
          })}
        >
          {inQueue && <BuildTriggeredBy buildId={buildId} className={styles.triggeredBy} />}
          <BuildCommentByIcon
            buildId={buildId}
            className={classnames(styles.comment, {
              [styles.separateFromTop]: inQueue,
            })}
            inline
          />
          <BuildStatusChangeComment buildId={buildId} className={styles.comment} inline />
        </div>
        <div className={styles.emptyLine} />
      </BuildLayout>
    </>
  )
}

export default Build
